import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Users } from "lucide-react";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { ShieldPreview } from "../components/shield/ShieldPreview";
import { ControlSections, NextButton } from "../components/designer/ControlSections";
import { ActionBar } from "../components/designer/ActionBar";
import { DraftsDialog } from "../components/designer/DraftsDialog";
import { SportPicker, LayoutPicker } from "../components/designer/SportPicker";
import { PreviewZoom } from "../components/designer/PreviewZoom";
import { MOBILE_TABS, MobileTabs, useIsMobile } from "../components/designer/MobileTabs";
import { useKeyboardOpen } from "../lib/useKeyboard";
import { trackDesignStart } from "../lib/analytics";
import { api, errMsg, rememberDraft, rememberTeam, getCoachToken } from "../lib/api";
import { exportPng } from "../lib/exportPng";
import { useSeo } from "../lib/seo";
import { pageFor, regionDesign } from "../lib/regionPages";
import { defaultDesign, newPlayerFrom, switchSport, sizeOf, shieldMm } from "../lib/shield";

const titleOf = (d) => d.player.name.trim() || "Untitled shield";
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "shield";

const SizeNote = ({ size }) => {
  const s = sizeOf(size);
  const m = shieldMm(s);
  return (
    <p className="mt-2 hidden text-center lg:block text-xs font-semibold text-slate-600" data-testid="preview-size-note">
      {s.name} · shield size {m.w} × {m.h} mm
    </p>
  );
};

export default function Designer() {
  const { id } = useParams();
  const nav = useNavigate();
  const svgRef = useRef(null);
  useSeo(id ? { title: "Your shield draft | sportsshields.ie", description: "Private shield draft.", noindex: true }
    : { title: "Design Your Personalised Player Shield | sportsshields.ie", path: "/design",
      description: "Free online shield designer: pick a background and club colours, add a photo, name and stats, preview live and order in four sizes." });
  const [design, setDesign] = useState(() => {
    const [sport, slug] = (new URLSearchParams(window.location.search).get("region") || "").split("/");
    const p = !id && slug && pageFor(sport, slug);
    return p ? regionDesign(p) : defaultDesign();
  });
  const [draftId, setDraftId] = useState(null);
  const [teamId, setTeamId] = useState(null);
  const [ordering, setOrdering] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const mobile = useIsMobile();
  const [tab, setTab] = useState("style");
  useEffect(() => { if (mobile) window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, [tab, mobile]);
  const [draftsOpen, setDraftsOpen] = useState(false);

  const set = (key) => (value) => setDesign((d) => ({ ...d, [key]: value }));

  useEffect(() => {
    if (!id || id === draftId) return;
    api.get(`/drafts/${id}`)
      .then(({ data }) => { setDesign({ ...defaultDesign(), ...data.design }); setDraftId(data.id); setTeamId(data.team_id || null); rememberDraft(data); toast.success(`Opened “${data.title}”`); })
      .catch((e) => { toast.error(errMsg(e)); setDesign(defaultDesign()); setDraftId(null); setTeamId(null); nav("/design", { replace: true }); });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (quiet, team = teamId) => {
    setSaving(true);
    try {
      const body = { title: titleOf(design), design, team_id: team };
      const { data } = draftId ? await api.put(`/drafts/${draftId}`, body) : await api.post("/drafts", body);
      rememberDraft(data);
      if (!draftId) { setDraftId(data.id); nav(`/d/${data.id}`, { replace: true }); }
      if (!quiet) toast.success(`Draft saved · code ${data.id}`);
      return data;
    } catch (e) { toast.error(errMsg(e)); return null; } finally { setSaving(false); }
  };

  const ensureTeam = async () => {
    if (teamId) return teamId;
    if (!getCoachToken()) return null;
    const { data } = await api.post("/teams", { name: design.player.clubName.trim() || "My squad" });
    rememberTeam(data);
    setTeamId(data.id);
    return data.id;
  };

  const another = async () => {
    let saved = null;
    try { saved = await save(true, await ensureTeam()); } catch (e) { toast.error(errMsg(e)); }
    if (!saved) return;
    setDesign(newPlayerFrom(design));
    setDraftId(null);
    nav("/design", { replace: true });
    toast.success(`Saved “${saved.title}”. New player started with the same club look.`);
  };

  const fresh = () => { setDesign(defaultDesign()); setDraftId(null); setTeamId(null); nav("/design"); };

  const order = async () => {
    setOrdering(true);
    const saved = await save(true);
    setOrdering(false);
    if (saved) nav(`/order?d=${saved.id}`);
  };

  const download = async () => {
    setDownloading(true);
    try { await exportPng(svgRef.current, `${slug(titleOf(design))}-shield.png`); } catch { toast.error("Couldn't create the preview image. Please try again."); } finally { setDownloading(false); }
  };

  const onDrag = (target, x, y) =>
    setDesign((d) => (target === "photo" ? { ...d, photo: { ...d.photo, x, y } } : { ...d, background: { ...d.background, upload: { ...d.background.upload, x, y } } }));

  const actions = { getSvg: () => svgRef.current, playerName: design.player.name.trim(), onOrder: order, ordering, onSave: () => save(false), onDownload: download, onAnother: another, saving, downloading, guide: design.guide, onGuide: set("guide") };

  const touched = useRef(false);
  useEffect(() => { if (touched.current) trackDesignStart(); }, [design]);
  const typing = useKeyboardOpen(mobile);
  const pickTab = (t) => setTab(t);
  const nextTab = MOBILE_TABS[MOBILE_TABS.findIndex(([k]) => k === tab) + 1];
  const next = nextTab ? <NextButton label={nextTab[1]} onClick={() => pickTab(nextTab[0])} /> : <NextButton label="Order your shield" onClick={order} />;
  return (
    <div className="min-h-screen pb-20 lg:pb-0">
      <Header onDrafts={() => setDraftsOpen(true)} onNew={fresh} />
      <main onPointerDownCapture={() => { touched.current = true; }} onKeyDownCapture={() => { touched.current = true; }} className="mx-auto grid max-w-[1500px] gap-6 px-4 py-4 sm:px-6 lg:grid-cols-[minmax(420px,1fr)_minmax(0,1.05fr)] lg:gap-10 lg:py-8">
        <section className="sticky top-[70px] z-30 -mx-4 border-b-2 border-slate-900 bg-slate-100 px-4 py-3 lg:order-2 lg:mx-0 lg:self-start lg:border-2 lg:bg-white lg:p-6 lg:shadow-[6px_6px_0_0_#0f172a] lg:top-24" data-testid="preview-panel">
          <div className="mb-3 hidden items-end justify-between lg:flex">
            <div><div className="eyebrow">Live preview</div><div className="font-display text-2xl font-black uppercase">{titleOf(design)}</div></div>
            <div className="flex items-center gap-2">
              {teamId && <Link to={`/team/${teamId}`} className="flex items-center gap-1.5 border-2 border-slate-900 px-2 py-1 text-xs font-bold uppercase hover:bg-slate-900 hover:text-white" data-testid="view-squad-link"><Users className="h-3.5 w-3.5" /> View squad</Link>}
              {draftId && <span className="font-mono-cc bg-slate-100 px-2 py-1 text-xs" data-testid="draft-code-badge">Draft {draftId}</span>}
            </div>
          </div>
          <PreviewZoom className={`checker mx-auto transition-[height] duration-200 ${typing ? "h-[14vh]" : "h-[30vh]"} lg:h-[calc(100vh-17rem)] lg:min-h-[480px]`}>
            <ShieldPreview ref={svgRef} design={design} onDrag={onDrag} />
          </PreviewZoom>
          {!typing && <>
            <SizeNote size={design.size} />
            {teamId && <Link to={`/team/${teamId}`} className="mt-1 block text-center text-xs font-bold uppercase text-slate-600 underline lg:hidden" data-testid="mobile-view-squad-link">View squad</Link>}
            {mobile && <MobileTabs tab={tab} onTab={pickTab} />}
          </>}
          <div className="mt-5 hidden lg:block"><ActionBar prefix="desktop" {...actions} /></div>
        </section>
        <section className="lg:order-1" data-testid="controls-panel">
          <div className="mb-5 hidden lg:block">
            <div className="eyebrow">Shield designer</div>
            <h1 className="font-display text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl">Build your player shield</h1>
            <p className="mt-2 max-w-lg text-sm text-slate-600">Choose a background, apply club colours, add the player, enter the statistics – and watch it come to life.</p>
          </div>
          {(!mobile || tab === "style") && <>
            <SportPicker sport={design.sport} onChange={(sp) => setDesign((d) => switchSport(d, sp))} />
            <LayoutPicker layout={design.layout} onChange={set("layout")} />
            {mobile && next}
          </>}
          {(!mobile || tab !== "style") && <ControlSections design={design} set={set} tab={mobile ? tab : undefined} next={next} />}
        </section>
      </main>
      <div className={`fixed inset-x-0 bottom-0 z-40 border-t-2 border-slate-900 bg-white p-3 lg:hidden ${typing ? "hidden" : ""}`} data-testid="mobile-action-bar"><ActionBar prefix="mobile" compact {...actions} /></div>
      <DraftsDialog open={draftsOpen} onClose={() => setDraftsOpen(false)} onOpen={(d) => { setDraftsOpen(false); nav(`/d/${d}`); }} />
    </div>
  );
}
